/**
 * Jertscript2 TypeScript Code Generator
 *
 * Transforms the Jertscript2 AST into valid TypeScript code.
 * Maps French keywords to their TypeScript equivalents.
 */

import type {
  Program,
  Statement,
  Expression,
  TypeNode,
  SimpleType,
  ArrayType,
  GenericType,
  UnionType,
  FunctionType,
  Literal,
  Identifier,
  BinaryExpression,
  UnaryExpression,
  CallExpression,
  MemberExpression,
  AssignmentExpression,
  ArrayExpression,
  ObjectExpression,
  NewExpression,
  AwaitExpression,
  ThisExpression,
  GroupingExpression,
  TypeAssertion,
  ArrowFunction,
  VariableDeclaration,
  FunctionDeclaration,
  IfStatement,
  WhileStatement,
  ForStatement,
  ReturnStatement,
  BlockStatement,
  TryStatement,
  ClassDeclaration,
  ImportStatement,
  ExportStatement,
  BreakStatement,
  ContinueStatement,
  ThrowStatement,
  ExpressionStatement,
  InterfaceDeclaration,
  TypeAliasDeclaration,
  EnumDeclaration,
  ClassMember,
  MethodDefinition,
  PropertyDefinition,
  ConstructorDefinition,
  PropertySignature,
  MethodSignature,
  Visibility,
} from '../parser/ast.js';

/**
 * Built-in function mappings from Jertscript to TypeScript
 */
const BUILTIN_FUNCTIONS: Record<string, string> = {
  dire: 'console.log',
  crier: 'console.error',
  avertir: 'console.warn',
  demander: 'prompt',
};

/**
 * Type mapping from French keywords to TypeScript types
 */
const TYPE_MAPPINGS: Record<string, string> = {
  nombre: 'number',
  texte: 'string',
  booleen: 'boolean',
  quelconque: 'any',
  inconnu: 'unknown',
  jamais: 'never',
  vide: 'void',
  rien: 'null',
  indefini: 'undefined',
  Tableau: 'Array',
  Promesse: 'Promise',
  Objet: 'object',
};

/**
 * Operator mapping from French keywords to TypeScript operators
 */
const OPERATOR_MAPPINGS: Record<string, string> = {
  et: '&&',
  ou: '||',
  pas: '!',
  est: '===',
  nest_pas: '!==',
  egal: '==',
  different: '!=',
};

/**
 * Visibility modifier mapping
 */
const VISIBILITY_MAPPINGS: Record<Visibility, string> = {
  prive: 'private',
  publique: 'public',
  protege: 'protected',
};

/**
 * Generator class that transforms a Jertscript2 AST into TypeScript code
 */
export class Generator {
  private indentLevel: number = 0;
  private indentString: string = '  '; // 2 spaces per indent level

  /**
   * Generate TypeScript code from the AST
   * @param program The program AST to generate code from
   * @returns The generated TypeScript code as a string
   */
  generate(program: Program): string {
    this.indentLevel = 0;
    const statements = program.body.map((stmt) => this.visitStatement(stmt));
    return statements.join('\n');
  }

  /**
   * Get the current indentation string
   */
  private indent(): string {
    return this.indentString.repeat(this.indentLevel);
  }

  /**
   * Increase indentation level
   */
  private increaseIndent(): void {
    this.indentLevel++;
  }

  /**
   * Decrease indentation level
   */
  private decreaseIndent(): void {
    if (this.indentLevel > 0) {
      this.indentLevel--;
    }
  }

  // ============================================================================
  // Type Generation
  // ============================================================================

  /**
   * Generate TypeScript type from a type node
   */
  private mapType(type: TypeNode): string {
    switch (type.type) {
      case 'SimpleType':
        return this.mapSimpleType(type as SimpleType);
      case 'ArrayType':
        return this.mapArrayType(type as ArrayType);
      case 'GenericType':
        return this.mapGenericType(type as GenericType);
      case 'UnionType':
        return this.mapUnionType(type as UnionType);
      case 'FunctionType':
        return this.mapFunctionType(type as FunctionType);
      default:
        throw new Error(`Unknown type node: ${(type as TypeNode).type}`);
    }
  }

  /**
   * Map a simple type name
   */
  private mapSimpleType(type: SimpleType): string {
    return TYPE_MAPPINGS[type.name] || type.name;
  }

  /**
   * Map an array type
   */
  private mapArrayType(type: ArrayType): string {
    const elementType = this.mapType(type.elementType);
    return `${elementType}[]`;
  }

  /**
   * Map a generic type
   */
  private mapGenericType(type: GenericType): string {
    const name = TYPE_MAPPINGS[type.name] || type.name;
    const typeArgs = type.typeArguments.map((t) => this.mapType(t)).join(', ');
    return `${name}<${typeArgs}>`;
  }

  /**
   * Map a union type
   */
  private mapUnionType(type: UnionType): string {
    return type.types.map((t) => this.mapType(t)).join(' | ');
  }

  /**
   * Map a function type
   */
  private mapFunctionType(type: FunctionType): string {
    const params = type.parameters
      .map((p) => `${p.name}: ${this.mapType(p.typeAnnotation)}`)
      .join(', ');
    const returnType = this.mapType(type.returnType);
    return `(${params}) => ${returnType}`;
  }

  // ============================================================================
  // Statement Visitors
  // ============================================================================

  /**
   * Visit any statement node and dispatch to the appropriate visitor
   */
  private visitStatement(stmt: Statement): string {
    switch (stmt.type) {
      case 'VariableDeclaration':
        return this.visitVariableDeclaration(stmt as VariableDeclaration);
      case 'FunctionDeclaration':
        return this.visitFunctionDeclaration(stmt as FunctionDeclaration);
      case 'IfStatement':
        return this.visitIfStatement(stmt as IfStatement);
      case 'WhileStatement':
        return this.visitWhileStatement(stmt as WhileStatement);
      case 'ForStatement':
        return this.visitForStatement(stmt as ForStatement);
      case 'ReturnStatement':
        return this.visitReturnStatement(stmt as ReturnStatement);
      case 'BlockStatement':
        return this.visitBlockStatement(stmt as BlockStatement);
      case 'TryStatement':
        return this.visitTryStatement(stmt as TryStatement);
      case 'ClassDeclaration':
        return this.visitClassDeclaration(stmt as ClassDeclaration);
      case 'ImportStatement':
        return this.visitImportStatement(stmt as ImportStatement);
      case 'ExportStatement':
        return this.visitExportStatement(stmt as ExportStatement);
      case 'BreakStatement':
        return this.visitBreakStatement();
      case 'ContinueStatement':
        return this.visitContinueStatement();
      case 'ThrowStatement':
        return this.visitThrowStatement(stmt as ThrowStatement);
      case 'ExpressionStatement':
        return this.visitExpressionStatement(stmt as ExpressionStatement);
      case 'InterfaceDeclaration':
        return this.visitInterfaceDeclaration(stmt as InterfaceDeclaration);
      case 'TypeAliasDeclaration':
        return this.visitTypeAliasDeclaration(stmt as TypeAliasDeclaration);
      case 'EnumDeclaration':
        return this.visitEnumDeclaration(stmt as EnumDeclaration);
      default:
        throw new Error(`Unknown statement type: ${(stmt as Statement).type}`);
    }
  }

  /**
   * Visit variable declaration
   * croissant -> const, baguette -> let
   */
  private visitVariableDeclaration(stmt: VariableDeclaration): string {
    const keyword = stmt.kind === 'croissant' ? 'const' : 'let';
    const typeAnnotation = stmt.typeAnnotation
      ? `: ${this.mapType(stmt.typeAnnotation)}`
      : '';
    const initializer = stmt.initializer
      ? ` = ${this.visitExpression(stmt.initializer)}`
      : '';
    return `${this.indent()}${keyword} ${stmt.name}${typeAnnotation}${initializer};`;
  }

  /**
   * Visit function declaration
   * fonction -> function, asynchrone fonction -> async function
   */
  private visitFunctionDeclaration(stmt: FunctionDeclaration): string {
    const asyncKeyword = stmt.async ? 'async ' : '';
    const params = stmt.params
      .map((p) => {
        const type = p.typeAnnotation ? `: ${this.mapType(p.typeAnnotation)}` : '';
        return `${p.name}${type}`;
      })
      .join(', ');
    const returnType = stmt.returnType ? `: ${this.mapType(stmt.returnType)}` : '';

    const lines: string[] = [];
    lines.push(
      `${this.indent()}${asyncKeyword}function ${stmt.name}(${params})${returnType} {`
    );

    this.increaseIndent();
    for (const bodyStmt of stmt.body.statements) {
      lines.push(this.visitStatement(bodyStmt));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit if statement
   * franchement -> if, sinon -> else
   */
  private visitIfStatement(stmt: IfStatement): string {
    const condition = this.visitExpression(stmt.condition);
    const lines: string[] = [];

    lines.push(`${this.indent()}if (${condition}) {`);

    this.increaseIndent();
    if (stmt.thenBranch.type === 'BlockStatement') {
      const block = stmt.thenBranch as BlockStatement;
      for (const s of block.statements) {
        lines.push(this.visitStatement(s));
      }
    } else {
      lines.push(this.visitStatement(stmt.thenBranch));
    }
    this.decreaseIndent();

    if (stmt.elseBranch) {
      if (stmt.elseBranch.type === 'IfStatement') {
        // else if case
        lines.push(
          `${this.indent()}} else ${this.visitIfStatement(stmt.elseBranch as IfStatement).trimStart()}`
        );
        return lines.join('\n');
      } else {
        lines.push(`${this.indent()}} else {`);

        this.increaseIndent();
        if (stmt.elseBranch.type === 'BlockStatement') {
          const block = stmt.elseBranch as BlockStatement;
          for (const s of block.statements) {
            lines.push(this.visitStatement(s));
          }
        } else {
          lines.push(this.visitStatement(stmt.elseBranch));
        }
        this.decreaseIndent();

        lines.push(`${this.indent()}}`);
      }
    } else {
      lines.push(`${this.indent()}}`);
    }

    return lines.join('\n');
  }

  /**
   * Visit while statement
   * pendant -> while
   */
  private visitWhileStatement(stmt: WhileStatement): string {
    const condition = this.visitExpression(stmt.condition);
    const lines: string[] = [];

    lines.push(`${this.indent()}while (${condition}) {`);

    this.increaseIndent();
    if (stmt.body.type === 'BlockStatement') {
      const block = stmt.body as BlockStatement;
      for (const s of block.statements) {
        lines.push(this.visitStatement(s));
      }
    } else {
      lines.push(this.visitStatement(stmt.body));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit for statement
   * pour -> for
   */
  private visitForStatement(stmt: ForStatement): string {
    let init = '';
    if (stmt.initializer) {
      // Remove the trailing semicolon and indentation from variable declaration
      init = this.visitStatement(stmt.initializer).trim().replace(/;$/, '');
    }

    const condition = stmt.condition ? this.visitExpression(stmt.condition) : '';
    const increment = stmt.increment ? this.visitExpression(stmt.increment) : '';

    const lines: string[] = [];
    lines.push(`${this.indent()}for (${init}; ${condition}; ${increment}) {`);

    this.increaseIndent();
    if (stmt.body.type === 'BlockStatement') {
      const block = stmt.body as BlockStatement;
      for (const s of block.statements) {
        lines.push(this.visitStatement(s));
      }
    } else {
      lines.push(this.visitStatement(stmt.body));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit return statement
   * retourner -> return
   */
  private visitReturnStatement(stmt: ReturnStatement): string {
    if (stmt.value) {
      return `${this.indent()}return ${this.visitExpression(stmt.value)};`;
    }
    return `${this.indent()}return;`;
  }

  /**
   * Visit block statement
   */
  private visitBlockStatement(stmt: BlockStatement): string {
    const lines: string[] = [];
    lines.push(`${this.indent()}{`);

    this.increaseIndent();
    for (const s of stmt.statements) {
      lines.push(this.visitStatement(s));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit try-catch-finally statement
   * malheureusement -> try, merde -> catch, quandmeme -> finally
   */
  private visitTryStatement(stmt: TryStatement): string {
    const lines: string[] = [];

    // Try block
    lines.push(`${this.indent()}try {`);
    this.increaseIndent();
    for (const s of stmt.tryBlock.statements) {
      lines.push(this.visitStatement(s));
    }
    this.decreaseIndent();

    // Catch block
    if (stmt.catchBlock) {
      const catchParam = stmt.catchParam || 'e';
      lines.push(`${this.indent()}} catch (${catchParam}) {`);
      this.increaseIndent();
      for (const s of stmt.catchBlock.statements) {
        lines.push(this.visitStatement(s));
      }
      this.decreaseIndent();
    }

    // Finally block
    if (stmt.finallyBlock) {
      if (!stmt.catchBlock) {
        lines.push(`${this.indent()}}`);
      }
      lines.push(`${this.indent()}} finally {`);
      this.increaseIndent();
      for (const s of stmt.finallyBlock.statements) {
        lines.push(this.visitStatement(s));
      }
      this.decreaseIndent();
    }

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit class declaration
   * classe -> class
   */
  private visitClassDeclaration(stmt: ClassDeclaration): string {
    const lines: string[] = [];

    let classLine = `${this.indent()}class ${stmt.name}`;

    if (stmt.superClass) {
      classLine += ` extends ${stmt.superClass}`;
    }

    if (stmt.implements && stmt.implements.length > 0) {
      classLine += ` implements ${stmt.implements.join(', ')}`;
    }

    lines.push(`${classLine} {`);

    this.increaseIndent();
    for (const member of stmt.members) {
      lines.push(this.visitClassMember(member));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit class member (method, property, or constructor)
   */
  private visitClassMember(member: ClassMember): string {
    switch (member.type) {
      case 'MethodDefinition':
        return this.visitMethodDefinition(member as MethodDefinition);
      case 'PropertyDefinition':
        return this.visitPropertyDefinition(member as PropertyDefinition);
      case 'ConstructorDefinition':
        return this.visitConstructorDefinition(member as ConstructorDefinition);
      default:
        throw new Error(`Unknown class member type: ${(member as ClassMember).type}`);
    }
  }

  /**
   * Visit method definition
   */
  private visitMethodDefinition(method: MethodDefinition): string {
    const visibility = method.visibility
      ? `${VISIBILITY_MAPPINGS[method.visibility]} `
      : '';
    const staticKeyword = method.static ? 'static ' : '';
    const asyncKeyword = method.async ? 'async ' : '';
    const params = method.params
      .map((p) => {
        const type = p.typeAnnotation ? `: ${this.mapType(p.typeAnnotation)}` : '';
        return `${p.name}${type}`;
      })
      .join(', ');
    const returnType = method.returnType ? `: ${this.mapType(method.returnType)}` : '';

    const lines: string[] = [];
    lines.push(
      `${this.indent()}${visibility}${staticKeyword}${asyncKeyword}${method.name}(${params})${returnType} {`
    );

    this.increaseIndent();
    for (const stmt of method.body.statements) {
      lines.push(this.visitStatement(stmt));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit property definition
   */
  private visitPropertyDefinition(prop: PropertyDefinition): string {
    const visibility = prop.visibility
      ? `${VISIBILITY_MAPPINGS[prop.visibility]} `
      : '';
    const staticKeyword = prop.static ? 'static ' : '';
    const readonlyKeyword = prop.readonly ? 'readonly ' : '';
    const typeAnnotation = prop.typeAnnotation
      ? `: ${this.mapType(prop.typeAnnotation)}`
      : '';
    const initializer = prop.initializer
      ? ` = ${this.visitExpression(prop.initializer)}`
      : '';

    return `${this.indent()}${visibility}${staticKeyword}${readonlyKeyword}${prop.name}${typeAnnotation}${initializer};`;
  }

  /**
   * Visit constructor definition
   */
  private visitConstructorDefinition(ctor: ConstructorDefinition): string {
    const params = ctor.params
      .map((p) => {
        const type = p.typeAnnotation ? `: ${this.mapType(p.typeAnnotation)}` : '';
        return `${p.name}${type}`;
      })
      .join(', ');

    const lines: string[] = [];
    lines.push(`${this.indent()}constructor(${params}) {`);

    this.increaseIndent();
    for (const stmt of ctor.body.statements) {
      lines.push(this.visitStatement(stmt));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit interface declaration
   * interface -> interface
   */
  private visitInterfaceDeclaration(stmt: InterfaceDeclaration): string {
    const lines: string[] = [];

    let interfaceLine = `${this.indent()}interface ${stmt.name}`;

    if (stmt.extends && stmt.extends.length > 0) {
      interfaceLine += ` extends ${stmt.extends.join(', ')}`;
    }

    lines.push(`${interfaceLine} {`);

    this.increaseIndent();
    for (const member of stmt.members) {
      if ('params' in member) {
        // It's a method signature
        lines.push(this.visitMethodSignature(member as MethodSignature));
      } else {
        // It's a property signature
        lines.push(this.visitPropertySignature(member as PropertySignature));
      }
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit property signature in interface
   */
  private visitPropertySignature(prop: PropertySignature): string {
    const readonlyKeyword = prop.readonly ? 'readonly ' : '';
    const optional = prop.optional ? '?' : '';
    const type = this.mapType(prop.typeAnnotation);
    return `${this.indent()}${readonlyKeyword}${prop.name}${optional}: ${type};`;
  }

  /**
   * Visit method signature in interface
   */
  private visitMethodSignature(method: MethodSignature): string {
    const optional = method.optional ? '?' : '';
    const params = method.params
      .map((p) => {
        const type = p.typeAnnotation ? `: ${this.mapType(p.typeAnnotation)}` : '';
        return `${p.name}${type}`;
      })
      .join(', ');
    const returnType = method.returnType ? `: ${this.mapType(method.returnType)}` : '';
    return `${this.indent()}${method.name}${optional}(${params})${returnType};`;
  }

  /**
   * Visit type alias declaration
   * type -> type
   */
  private visitTypeAliasDeclaration(stmt: TypeAliasDeclaration): string {
    const type = this.mapType(stmt.typeValue);
    return `${this.indent()}type ${stmt.name} = ${type};`;
  }

  /**
   * Visit enum declaration
   * enumeration -> enum
   */
  private visitEnumDeclaration(stmt: EnumDeclaration): string {
    const lines: string[] = [];
    lines.push(`${this.indent()}enum ${stmt.name} {`);

    this.increaseIndent();
    for (let i = 0; i < stmt.members.length; i++) {
      const member = stmt.members[i];
      const comma = i < stmt.members.length - 1 ? ',' : '';
      if (member.value) {
        lines.push(
          `${this.indent()}${member.name} = ${this.visitExpression(member.value)}${comma}`
        );
      } else {
        lines.push(`${this.indent()}${member.name}${comma}`);
      }
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit import statement
   * importer -> import, de -> from
   */
  private visitImportStatement(stmt: ImportStatement): string {
    const parts: string[] = [];

    if (stmt.defaultImport) {
      parts.push(stmt.defaultImport);
    }

    if (stmt.namespaceImport) {
      parts.push(`* as ${stmt.namespaceImport}`);
    }

    if (stmt.specifiers.length > 0) {
      const specifiers = stmt.specifiers
        .map((s) =>
          s.imported === s.local ? s.imported : `${s.imported} as ${s.local}`
        )
        .join(', ');
      parts.push(`{ ${specifiers} }`);
    }

    const importClause = parts.join(', ');
    return `${this.indent()}import ${importClause} from "${stmt.source}";`;
  }

  /**
   * Visit export statement
   * exporter -> export
   */
  private visitExportStatement(stmt: ExportStatement): string {
    if (stmt.declaration) {
      const decl = this.visitStatement(stmt.declaration).trimStart();
      const exportKeyword = stmt.isDefault ? 'export default' : 'export';
      return `${this.indent()}${exportKeyword} ${decl}`;
    }

    if (stmt.specifiers && stmt.specifiers.length > 0) {
      const specifiers = stmt.specifiers
        .map((s) =>
          s.local === s.exported ? s.local : `${s.local} as ${s.exported}`
        )
        .join(', ');
      return `${this.indent()}export { ${specifiers} };`;
    }

    return '';
  }

  /**
   * Visit break statement
   * casser -> break
   */
  private visitBreakStatement(): string {
    return `${this.indent()}break;`;
  }

  /**
   * Visit continue statement
   * continuer -> continue
   */
  private visitContinueStatement(): string {
    return `${this.indent()}continue;`;
  }

  /**
   * Visit throw statement
   * lancer -> throw
   */
  private visitThrowStatement(stmt: ThrowStatement): string {
    return `${this.indent()}throw ${this.visitExpression(stmt.argument)};`;
  }

  /**
   * Visit expression statement
   */
  private visitExpressionStatement(stmt: ExpressionStatement): string {
    return `${this.indent()}${this.visitExpression(stmt.expression)};`;
  }

  // ============================================================================
  // Expression Visitors
  // ============================================================================

  /**
   * Visit any expression node and dispatch to the appropriate visitor
   */
  private visitExpression(expr: Expression): string {
    switch (expr.type) {
      case 'Literal':
        return this.visitLiteral(expr as Literal);
      case 'Identifier':
        return this.visitIdentifier(expr as Identifier);
      case 'BinaryExpression':
        return this.visitBinaryExpression(expr as BinaryExpression);
      case 'UnaryExpression':
        return this.visitUnaryExpression(expr as UnaryExpression);
      case 'CallExpression':
        return this.visitCallExpression(expr as CallExpression);
      case 'MemberExpression':
        return this.visitMemberExpression(expr as MemberExpression);
      case 'AssignmentExpression':
        return this.visitAssignmentExpression(expr as AssignmentExpression);
      case 'ArrayExpression':
        return this.visitArrayExpression(expr as ArrayExpression);
      case 'ObjectExpression':
        return this.visitObjectExpression(expr as ObjectExpression);
      case 'NewExpression':
        return this.visitNewExpression(expr as NewExpression);
      case 'AwaitExpression':
        return this.visitAwaitExpression(expr as AwaitExpression);
      case 'ThisExpression':
        return this.visitThisExpression();
      case 'GroupingExpression':
        return this.visitGroupingExpression(expr as GroupingExpression);
      case 'TypeAssertion':
        return this.visitTypeAssertion(expr as TypeAssertion);
      case 'ArrowFunction':
        return this.visitArrowFunction(expr as ArrowFunction);
      default:
        throw new Error(`Unknown expression type: ${(expr as Expression).type}`);
    }
  }

  /**
   * Visit literal expression
   * oui -> true, non -> false, rien -> null
   */
  private visitLiteral(expr: Literal): string {
    if (expr.value === null) {
      return 'null';
    }
    if (typeof expr.value === 'string') {
      // Properly escape the string
      const escaped = expr.value
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\t/g, '\\t')
        .replace(/\r/g, '\\r');
      return `"${escaped}"`;
    }
    if (typeof expr.value === 'boolean') {
      return expr.value ? 'true' : 'false';
    }
    return String(expr.value);
  }

  /**
   * Visit identifier expression
   */
  private visitIdentifier(expr: Identifier): string {
    return expr.name;
  }

  /**
   * Visit binary expression
   * Handle et -> &&, ou -> ||, est -> ===, nest_pas -> !==
   */
  private visitBinaryExpression(expr: BinaryExpression): string {
    const left = this.visitExpression(expr.left);
    const right = this.visitExpression(expr.right);
    const op = this.translateOperator(expr.operator);
    return `${left} ${op} ${right}`;
  }

  /**
   * Visit unary expression
   * Handle pas -> !
   */
  private visitUnaryExpression(expr: UnaryExpression): string {
    const operand = this.visitExpression(expr.operand);
    const op = this.translateOperator(expr.operator);
    return `${op}${operand}`;
  }

  /**
   * Visit call expression
   * Handle built-in function transforms: dire() -> console.log()
   */
  private visitCallExpression(expr: CallExpression): string {
    let callee = this.visitExpression(expr.callee);

    // Check if this is a built-in function
    if (expr.callee.type === 'Identifier') {
      const name = (expr.callee as Identifier).name;
      if (BUILTIN_FUNCTIONS[name]) {
        callee = BUILTIN_FUNCTIONS[name];
      }
    }

    const args = expr.arguments.map((arg) => this.visitExpression(arg)).join(', ');
    return `${callee}(${args})`;
  }

  /**
   * Visit member expression
   * obj.property or obj[expr]
   */
  private visitMemberExpression(expr: MemberExpression): string {
    const object = this.visitExpression(expr.object);

    if (expr.computed) {
      const property = this.visitExpression(expr.property);
      return `${object}[${property}]`;
    }

    const property = this.visitExpression(expr.property);
    return `${object}.${property}`;
  }

  /**
   * Visit assignment expression
   */
  private visitAssignmentExpression(expr: AssignmentExpression): string {
    const target = this.visitExpression(expr.target);
    const value = this.visitExpression(expr.value);
    return `${target} ${expr.operator} ${value}`;
  }

  /**
   * Visit array expression
   */
  private visitArrayExpression(expr: ArrayExpression): string {
    const elements = expr.elements.map((el) => this.visitExpression(el)).join(', ');
    return `[${elements}]`;
  }

  /**
   * Visit object expression
   */
  private visitObjectExpression(expr: ObjectExpression): string {
    if (expr.properties.length === 0) {
      return '{}';
    }

    const props = expr.properties
      .map((prop) => {
        if (prop.shorthand) {
          return this.visitExpression(prop.key);
        }

        let key: string;
        if (prop.computed) {
          key = `[${this.visitExpression(prop.key)}]`;
        } else if (prop.key.type === 'Identifier') {
          key = (prop.key as Identifier).name;
        } else if (prop.key.type === 'Literal') {
          const literal = prop.key as Literal;
          key = typeof literal.value === 'string' ? `"${literal.value}"` : String(literal.value);
        } else {
          key = this.visitExpression(prop.key);
        }

        const value = this.visitExpression(prop.value);
        return `${key}: ${value}`;
      })
      .join(', ');

    return `{ ${props} }`;
  }

  /**
   * Visit new expression
   * nouveau -> new
   */
  private visitNewExpression(expr: NewExpression): string {
    const callee = this.visitExpression(expr.callee);
    const args = expr.arguments.map((arg) => this.visitExpression(arg)).join(', ');
    return `new ${callee}(${args})`;
  }

  /**
   * Visit await expression
   * attendre -> await
   */
  private visitAwaitExpression(expr: AwaitExpression): string {
    return `await ${this.visitExpression(expr.argument)}`;
  }

  /**
   * Visit this expression
   * ceci -> this
   */
  private visitThisExpression(): string {
    return 'this';
  }

  /**
   * Visit grouping expression
   */
  private visitGroupingExpression(expr: GroupingExpression): string {
    return `(${this.visitExpression(expr.expression)})`;
  }

  /**
   * Visit type assertion
   * comme -> as
   */
  private visitTypeAssertion(expr: TypeAssertion): string {
    const expression = this.visitExpression(expr.expression);
    const type = this.mapType(expr.typeAnnotation);
    return `${expression} as ${type}`;
  }

  /**
   * Visit arrow function
   */
  private visitArrowFunction(expr: ArrowFunction): string {
    const asyncKeyword = expr.async ? 'async ' : '';
    const params = expr.params
      .map((p) => {
        const type = p.typeAnnotation ? `: ${this.mapType(p.typeAnnotation)}` : '';
        return `${p.name}${type}`;
      })
      .join(', ');
    const returnType = expr.returnType ? `: ${this.mapType(expr.returnType)}` : '';

    let body: string;
    if (expr.body.type === 'BlockStatement') {
      // Block body - need to generate full block
      const blockLines: string[] = [];
      blockLines.push('{');
      this.increaseIndent();
      for (const stmt of (expr.body as BlockStatement).statements) {
        blockLines.push(this.visitStatement(stmt));
      }
      this.decreaseIndent();
      blockLines.push(`${this.indent()}}`);
      body = blockLines.join('\n');
    } else {
      // Expression body
      body = this.visitExpression(expr.body as Expression);
    }

    return `${asyncKeyword}(${params})${returnType} => ${body}`;
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Translate Jertscript operators to TypeScript operators
   */
  private translateOperator(operator: string): string {
    return OPERATOR_MAPPINGS[operator] || operator;
  }
}

/**
 * Convenience function to generate TypeScript from a program AST
 */
export function generate(program: Program): string {
  const generator = new Generator();
  return generator.generate(program);
}
